import React from 'react';
import { useImmer } from 'use-immer';
import { Row, Col } from 'react-bootstrap';
import { useQuery, useMutation } from '@apollo/client';
import {
  Modal,
  ModalTitle,
  ModalContent,
  ModalFooter,
  TextInput,
  TextArea,
} from '@utahdts/utah-design-system';
import { GET_AVERAGE_ANNUAL_DEMAND_PER_ERC, SET_AVERAGE_ANNUAL_DEMAND_PER_ERC } from '../../../../../queries/engineeringQrys';
import { numbersUpToTwoDecimalPlaces, validateField } from '../../../../reuseableComponents/forms/formValidation';
import { formatNumber } from '../../dwriUtils';
import '../../dwri.css';

const initialFormState = {
  adjustedDemandValue: '',
  adjustedDemandError: '',
  changeJustificationValue: '',
  changeJustificationError: '',
};

const DwriAverageAnnualDemandPerErc = ({ pwsId, years = [], selectedSources = [], onSaved }) => {

  const [editRow, setEditRow] = React.useState(null);
  const [formState, setFormState] = useImmer(initialFormState);
  const [mutationError, setMutationError] = React.useState('');

  const user = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : { isPublic: true };

  const sourceFilter = selectedSources.length > 0 ? selectedSources : undefined;

  const [setAverageAnnualDemand] = useMutation(SET_AVERAGE_ANNUAL_DEMAND_PER_ERC);

  const { data, loading, error, refetch } = useQuery(GET_AVERAGE_ANNUAL_DEMAND_PER_ERC, {
    variables: { pwsId, years, sources: sourceFilter },
    skip: !pwsId || !years || years.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  if (error) return <p className="text-danger">Error loading Average Annual Demand per ERC</p>;
  if (loading) return <p>Loading...</p>;

  const rows = data?.getAverageAnnualDemandPerErc || [];

  function handleEditClick(row) {
    setEditRow(row);
    setFormState({
      ...initialFormState,
      adjustedDemandValue: row.adjustedAverageAnnualDemandErc == null ? '' : String(row.adjustedAverageAnnualDemandErc),
      changeJustificationValue: row.changeJustification ?? '',
    });
    setMutationError('');
  }

  function handleModalClose() {
    setEditRow(null);
    setFormState(initialFormState);
    setMutationError('');
  }

  function handleAdjustedDemandChange(event) {
    const nextValue = event.target.value;
    const validationResult = numbersUpToTwoDecimalPlaces(nextValue);
    setFormState((draft) => {
      draft.adjustedDemandValue = nextValue;
      draft.adjustedDemandError = validationResult === true ? '' : validationResult;
    });
  }

  function handleAdjustedDemandBlur(event) {
    const nextValue = event.target.value;
    const validationResult = numbersUpToTwoDecimalPlaces(nextValue);
    setFormState((draft) => {
      draft.adjustedDemandValue = nextValue;
      draft.adjustedDemandError = validationResult === true ? '' : validationResult;
    });
  }

  function isAdjustedDemandChanged() {
    if (!editRow) {
      return false;
    }
    const previousValue = editRow.adjustedAverageAnnualDemandErc == null ? '' : String(editRow.adjustedAverageAnnualDemandErc);
    return formState.adjustedDemandValue !== previousValue;
  }

  async function handleSave() {
    if (!editRow) return;

    const errors = {};
    validateField(errors, formState, 'adjustedDemandValue', (value) => value === '' || numbersUpToTwoDecimalPlaces(value) === true, 'Must be a number with up to 2 decimal places');
    validateField(errors, formState, 'changeJustificationValue', (value) => !isAdjustedDemandChanged() || !!value.trim(), 'Change Justification is required when Adjusted Average Annual Demand per ERC is changed.');

    if (Object.keys(errors).length > 0) {
      setFormState((draft) => {
        draft.adjustedDemandError = errors.adjustedDemandValue?.message || '';
        draft.changeJustificationError = errors.changeJustificationValue?.message || '';
      });
      return;
    }

    setMutationError('');
    try {
      const response = await setAverageAnnualDemand({
        variables: {
          updateAverageAnnualDemandInput: {
            pwsId,
            year: editRow.year,
            adjustedAverageAnnualDemandErc: formState.adjustedDemandValue === '' ? null : Number(formState.adjustedDemandValue),
            changeJustification: formState.changeJustificationValue,
          },
        },
        errorPolicy: 'all',
      });

      const responseErrorMessage = response?.errors?.[0]?.message;
      if (responseErrorMessage) {
        setMutationError(responseErrorMessage);
        return;
      }

      if (!response?.data?.updateAverageAnnualDemandPerErc) {
        setMutationError('Failed to update average annual demand per ERC.');
        return;
      }

      refetch();
      onSaved?.();
      handleModalClose();
    } catch (mutationErr) {
      setMutationError(mutationErr?.message || 'Failed to update average annual demand per ERC.');
    }
  }

  const canEdit = user?.permissions?.includes('edit_water_use_system');

  return (
    <>
      <Row>
        <Col><span><b>Average Annual Demand Per ERC (Gal/year)</b></span></Col>
      </Row>

      <div className="table-responsive">
        <Row className={`dwri-erc-table-header dwri-average-annual-demand-header gx-0 ${canEdit ? 'has-edit' : ''}`}>
          <Col className="dwri-table-header-cell dwri-col-year"><span><b>Year</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Total Sources (gal)</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Total Wholesale Sources (gal)</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Total Return Sources (gal)</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Source Less Wholesale (gal)</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Average Annual Demand per ERC</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Adjusted Average Annual Demand per ERC</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Change Justification</b></span></Col>
          {canEdit && (
            <Col className="dwri-table-header-cell dwri-col-edit"><span><b>Edit</b></span></Col>
          )}
        </Row>

        {rows.map((row, index) => (
          <Row
            key={row.year ?? index}
            className={`dwri-erc-table-row dwri-average-annual-demand-row gx-0 ${canEdit ? 'has-edit' : ''} ${index % 2 === 0 ? 'is-even' : 'is-odd'}`}
          >
            <Col className="dwri-table-cell dwri-col-year"><span>{row.year}</span></Col>
            <Col className="dwri-table-cell"><span>{formatNumber(row.totalFromSourcesGallons)}</span></Col>
            <Col className="dwri-table-cell"><span>{formatNumber(row.totalWholesaleSourcesGallons)}</span></Col>
            <Col className="dwri-table-cell"><span>{formatNumber(row.totalReturnSourcesGallons)}</span></Col>
            <Col className="dwri-table-cell"><span>{formatNumber(row.sourceLessWholesaleGallons)}</span></Col>
            <Col className="dwri-table-cell"><span>{formatNumber(row.averageAnnualDemandErc)}</span></Col>
            <Col className="dwri-table-cell"><span>{formatNumber(row.adjustedAverageAnnualDemandErc)}</span></Col>
            <Col className="dwri-table-cell"><span>{row.changeJustification}</span></Col>
            {canEdit && (
              <Col className="dwri-table-cell dwri-col-edit">
                <button
                  className="link-button"
                  aria-label={`Edit average annual demand per ERC for year ${row.year}`}
                  type="button"
                  onClick={() => handleEditClick(row)}
                >
                  Edit
                </button>
              </Col>
            )}
          </Row>
        ))}
      </div>

      {editRow && (
        <Modal
          onClose={handleModalClose}
          onEscape={handleModalClose}
        >
          <ModalTitle>Update Average Annual Demand per ERC</ModalTitle>
          <ModalContent>
            <div className="dwri-peak-day-demand-modal-grid">
              <div className="dwri-peak-day-demand-modal-row dwri-peak-day-demand-modal-row--two">
                <TextInput label="Year" value={String(editRow.year ?? '')} isDisabled />
                <TextInput label="Total Sources (gal)" value={formatNumber(editRow.totalFromSourcesGallons)} isDisabled />
              </div>
              <div className="dwri-peak-day-demand-modal-row dwri-peak-day-demand-modal-row--two">
                <TextInput label="Total Wholesale Sources (gal)" value={formatNumber(editRow.totalWholesaleSourcesGallons)} isDisabled />
                <TextInput label="Total Return Sources (gal)" value={formatNumber(editRow.totalReturnSourcesGallons)} isDisabled />
              </div>
              <div className="dwri-peak-day-demand-modal-row dwri-peak-day-demand-modal-row--two">
                <TextInput label="Source Less Wholesale (gal)" value={formatNumber(editRow.sourceLessWholesaleGallons)} isDisabled />
                <TextInput label="Average Annual Demand per ERC" value={formatNumber(editRow.averageAnnualDemandErc)} isDisabled />
              </div>
              <div className="dwri-peak-day-demand-modal-row dwri-peak-day-demand-modal-row--two">
                <TextInput
                  label="Adjusted Average Annual Demand per ERC"
                  value={formState.adjustedDemandValue}
                  onChange={handleAdjustedDemandChange}
                  onBlur={(event) => handleAdjustedDemandBlur(event)}
                  errorMessage={formState.adjustedDemandError || undefined}
                />
              </div>
              <div className="dwri-peak-day-demand-modal-row dwri-peak-day-demand-modal-row--full">
                <TextArea
                  label="Change Justification"
                  value={formState.changeJustificationValue}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setFormState((draft) => {
                      draft.changeJustificationValue = nextValue;
                      draft.changeJustificationError = '';
                    });
                  }}
                  isRequired={isAdjustedDemandChanged()}
                  errorMessage={formState.changeJustificationError || undefined}
                />
              </div>
            </div>
          </ModalContent>
          <ModalFooter className="dwri-service-modal-footer">
            {mutationError && (
              <div className="text-danger">{mutationError}</div>
            )}
            <button type="button" onClick={handleModalClose}>Close</button>
            <button type="button" onClick={handleSave}>Save</button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default DwriAverageAnnualDemandPerErc;

